/**
 * Constructeur d'URL embed pour le SDK widget.
 *
 * IMPORTANT : doit produire EXACTEMENT les mêmes URLs que
 * `dog-reservation-frontend/src/utils/embedBookingUrls.ts` (et donc que
 * `rintintin-backoffice5/src/utils/bookingWidgetSnippets.ts`). La duplication
 * est volontaire : le SDK est compilé en bundle indépendant et ne peut pas
 * importer du code React/CRA. Les tests `url.test.ts` vérifient la parité.
 */

import type { RintintinBookingInitOptions } from './types';

export const DEFAULT_WIDGET_BASE_URL = 'https://rintintin-booking.com';

/** Retire le slash final éventuel pour normaliser `https://foo.com/`. */
export function normalizeBaseUrl(baseUrl: string): string {
  return baseUrl.replace(/\/$/, '');
}

/**
 * Construit l'URL embed à charger dans l'iframe pour un pro et un set d'options.
 * Mêmes règles de filtrage / validation que `appendEmbedOptions` côté frontend.
 */
export function buildWidgetEmbedUrl(options: RintintinBookingInitOptions): string {
  const base = normalizeBaseUrl(options.baseUrl || DEFAULT_WIDGET_BASE_URL);
  const slug = encodeURIComponent(options.pro);
  const params = new URLSearchParams();

  // `source=widget` toujours présent : permet d'identifier l'origine en analytics
  params.set('source', 'widget');

  if (options.widget) params.set('widget', options.widget);
  if (options.service) params.set('service', options.service);

  const selectedServices = options.services ? options.services.filter(Boolean) : undefined;
  if (selectedServices && selectedServices.length > 0) {
    params.set('services', selectedServices.join(','));
  }

  const selectedForfaits = options.forfaits ? options.forfaits.filter(Boolean) : undefined;
  if (selectedForfaits && selectedForfaits.length > 0) {
    params.set('forfaits', selectedForfaits.join(','));
  }

  const selectedPacks = options.packs ? options.packs.filter(Boolean) : undefined;
  if (selectedPacks && selectedPacks.length > 0) {
    params.set('packs', selectedPacks.join(','));
  }

  if (options.layout) params.set('layout', options.layout);

  if (options.widget === 'forfaits' && options.forfaitCards) {
    params.set('forfaitCards', options.forfaitCards);
  }

  if (options.widget === 'credits' && options.creditCards) {
    params.set('creditCards', options.creditCards);
  }

  // accent : on accepte avec ou sans `#`, et on ne sérialise que si valide (6 hex)
  if (options.accent) {
    const accent = options.accent.replace(/^#/, '').trim();
    if (/^[0-9A-Fa-f]{6}$/.test(accent)) {
      params.set('accent', accent);
    }
  }

  if (options.radius != null && Number.isFinite(options.radius)) {
    params.set('radius', String(Math.round(options.radius as number)));
  }

  if (options.theme === 'dark') {
    params.set('theme', 'dark');
  }

  const bg = options.bg?.trim().toLowerCase();
  if (bg === 'transparent') {
    params.set('bg', 'transparent');
  } else if (bg) {
    const hex = bg.replace(/^#/, '');
    if (/^[0-9a-f]{6}$/.test(hex)) {
      params.set('bg', hex);
    }
  }

  const ctaBook = options.ctaBook?.trim().replace(/\s+/g, ' ').slice(0, 32);
  if (ctaBook && ctaBook !== 'Réserver' && ctaBook !== 'Boeken') {
    params.set('ctaBook', ctaBook);
  }

  if (options.header === 'compact') {
    params.set('header', 'compact');
  }

  if (options.widget === 'service' && options.presentation === 'button') {
    params.set('presentation', 'button');
  }

  if (options.padding != null && options.padding !== 8) {
    params.set('padding', String(options.padding));
  }

  if (options.checkoutInFrame) {
    params.set('checkoutInFrame', '1');
  }

  if (options.parentOrigin) {
    try {
      const origin = new URL(options.parentOrigin).origin;
      params.set('parentOrigin', origin);
    } catch {
      const trimmed = options.parentOrigin.trim();
      if (trimmed) params.set('parentOrigin', trimmed);
    }
  }

  return `${base}/embed/booking/${slug}?${params.toString()}`;
}

/**
 * Enrichit les options SDK avec checkout in-frame et l'origine du site hôte.
 * Appelé par popup / badge / inline avant `buildWidgetEmbedUrl`.
 */
export function withSdkHostContext(
  options: RintintinBookingInitOptions,
  hostOrigin?: string
): RintintinBookingInitOptions {
  const origin =
    hostOrigin || (typeof window !== 'undefined' ? window.location.origin : undefined);
  return {
    ...options,
    checkoutInFrame: true,
    ...(origin ? { parentOrigin: origin } : {}),
  };
}
